// ============================================================
// SendBillModal.tsx — Send bill: email an ISSUED bill's PDF to the client
// from the user's own Outlook mailbox (POST /api/invoices/:id/send — the same
// Microsoft Graph integration the compose modal uses).
//
// Opens pre-filled from GET /api/invoices/:id/send-draft:
//   To      — the client's email from their record (editable; a prompt asks
//             for one when the record has none)
//   Copy    — "Send a copy to me" (on by default) cc's the sender as a receipt
//   Subject / Message — the firm's template (Admin › Billing) with every merge
//             field already resolved; editable before sending
//   The bill PDF is rendered + attached by the SERVER (shown as a chip here).
// The server logs the send on the case's activity, records the first-delivery
// date (never moved by a resend) and adds it to the bill history.
// Chrome mirrors ComposeEmailModal: ModalFrame hero + cards, the amber
// connect / reconnect prompt with a link into Settings, and every failure
// keeps what the user typed and shows the real reason (Microsoft's own).
//
// B3 — delivery rules (the server enforces each on send too):
//   - unapproved bill wording blocks sending (admins get a link to
//     Settings › Bill wording);
//   - "Client has no email address" → "No email address on file", no form;
//   - email only to the address the client consented to (every To
//     recipient); otherwise "No email consent for this address…";
//   - a changed client email since consent → "Consent was given for…";
//   - each blocked case offers "Mark as sent by post / handed over".
//
// B3b — the same dialog sends an issued CREDIT NOTE (creditNote prop:
// /api/credit-notes/:id/send-draft + /send — same consent rules). Blocked
// states that last until something changes were role="status" (batch 2:
// the shared ActionBox — not live; the send result / errors speak).
// B3c — consent is recorded INSIDE this window (ConsentInline — no pop-up on
// a pop-up). On save the box turns green ("Consent recorded for …"), To is set
// to that address and consent is re-checked at once from what was saved, so
// Send unlocks with no refresh (B3b bug: To kept the old address → still
// locked). The draft is then re-read in the background to stay in step.
// (Batch 2) No email on file → To starts EMPTY (it used to show the old
// consented address); every warning names the document ("the credit note");
// the To error names the litigation friend when it goes to them; the amber
// notes are the shared ActionBox; the Message box starts ~8 lines and grows;
// nothing closes it while sending; a send announces caseChange('bill').
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import ModalFrame, { HERO_PILL_SOFT } from '../shared/ModalFrame'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import { navigateTo } from '../../navigation/navigate'
import { newSendKey } from '../../utils/sendKey'
import { getBillSendDraftApi, getCreditNoteSendDraftApi, prepareBillSendApi, recordLitigationFriendConsentApi, sendBillApi, sendCreditNoteApi, type BillSendDraft, type BillSendSuccess } from '../../api/casevoApi'
import type { Bill, CreditNote } from '../../types/billing.types'
import ConsentInline from './ConsentInline'
import ContactMismatchNote from '../shared/ContactMismatchNote'
import type { BillEmailConsentState } from '../../api/casevoApi'
import { BIcon, B_PRIMARY, B_SECONDARY, CARD, INPUT, NOTE, Skel, errorText, fmtDate } from './billingUi'
import { useDraftDirty, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { useCasevoStore } from '../../store/useCasevoStore'
import { NO_EMAIL_ON_FILE, recipientsMatchConsent } from '../../utils/noEmail'
import { isValidEmail } from '../../utils/contactFormat'
import ActionBox from '../shared/ActionBox'
import MessageTextarea from './MessageTextarea'
import { emitCaseChange } from '../../utils/caseChangeEvents'

type Field = 'to' | 'subject' | 'body'
// The shared email rule (utils/contactFormat) — the server checks the same.
const EMAIL = { test: isValidEmail }
const splitRecipients = (s: string): string[] => s.split(/[,;]/).map((x) => x.trim()).filter(Boolean)

// (Batch 2) The To a send window starts with: EMPTY when there's no email on
// file (the server's `to` can still hold an old consented address). Shared
// with the statement send window.
export const initialSendTo = (d: { to: string; clientHasEmail: boolean; noEmailAddress?: boolean }): string => (d.noEmailAddress || !d.clientHasEmail ? '' : d.to)
// "Enter the client's / the litigation friend's email address."
export const toRequiredMessage = (toLitigationFriend: boolean): string => `Enter the ${toLitigationFriend ? 'litigation friend' : 'client'}’s email address.`
// (Audit fix 2) The client's date of birth can't be read → no email — said in
// the document's own words.
export const capacityUnknownMessage = (noun: string): string =>
  `The client’s date of birth is missing or can’t be read, so we can’t tell whether the ${noun} should go to a litigation friend. Fix it in Client details first.`

// The amber notice (casevo-warn tokens — light + dark).

const SendBillModal: React.FC<{ bill: Bill; creditNote?: CreditNote; onClose: () => void; onSent: (r: BillSendSuccess) => void; onUsePost?: () => void }> = ({ bill, creditNote, onClose, onSent, onUsePost }) => {
  const toRef = useRef<HTMLInputElement | null>(null)
  const isAdmin = useCasevoStore((s) => s.authUser?.role === 'admin')
  // B3b — a credit note goes out exactly like a bill.
  const noun = creditNote ? 'credit note' : 'bill'
  const docNumber = creditNote ? creditNote.number : bill.number
  // B3c — the address consent was just recorded for (the green box).
  const [recorded, setRecorded] = useState<string | null>(null)
  const [draft, setDraft] = useState<BillSendDraft | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [to, setTo] = useState('')
  const [ccMe, setCcMe] = useState(true)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [touched, setTouched] = useState(false)
  const [serverField, setServerField] = useState<Partial<Record<Field, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [connectPrompt, setConnectPrompt] = useState<'connect' | 'reconnect' | null>(null)
  const [sending, setSending] = useState(false)
  const [phase, setPhase] = useState<'preparing' | 'sending' | null>(null)
  // (Audit fix batch 1, 31 Oct) One key for this window's send: a retry (a
  // dropped connection) repeats it and the server emails ONCE.
  const [sendKey] = useState(newSendKey)
  // Unsaved-changes guard: ×, Esc, backdrop, Cancel (and leaving the screen /
  // Back / refresh) ask before dropping what was typed here.
  const guardClose = useUnsavedChangesGuard(useDraftDirty(draft !== null, [to, ccMe, subject, body]) && !sending)
  const requestClose = (): void => guardClose(onClose)

  const cnId = creditNote?.id ?? null
  const fetchDraft = (): Promise<BillSendDraft> => (cnId ? getCreditNoteSendDraftApi(cnId) : getBillSendDraftApi(bill.id))
  useEffect(() => {
    let cancelled = false
    ;(cnId ? getCreditNoteSendDraftApi(cnId) : getBillSendDraftApi(bill.id))
      .then((d) => { if (cancelled) return; setDraft(d); setTo(initialSendTo(d)); setSubject(d.subject); setBody(d.body) })
      .catch((e) => { if (!cancelled) setLoadErr(errorText(e, 'Couldn’t prepare the email.')) })
    return () => { cancelled = true }
  }, [bill.id, cnId])
  // B3c — consent recorded in the box → re-check NOW from what was saved
  // (To = that address), then re-read the delivery facts in the background.
  // The subject / message the user has typed stay as they are.
  const consentSaved = async (st: BillEmailConsentState, email: string, dateIso: string, method: string): Promise<void> => {
    const agreed = st.billEmailConsentEmail || email
    const clientEmail = draft?.clientEmail ?? null
    setDraft((cur) => (cur ? {
      ...cur,
      consent: { at: st.billEmailConsentAt ?? `${dateIso}T00:00:00.000Z`, email: agreed, method: st.billEmailConsentMethod ?? method },
      consentChanged: !!clientEmail && clientEmail.trim().toLowerCase() !== agreed.trim().toLowerCase(),
      emailAllowed: true, emailBlockedReason: null,
    } : cur))
    setTo(agreed)
    setServerField((f) => ({ ...f, to: undefined }))
    setRecorded(agreed)
    try {
      const d = await fetchDraft()
      setDraft((cur) => (cur ? { ...cur, consent: d.consent, consentChanged: d.consentChanged, emailAllowed: d.emailAllowed, emailBlockedReason: d.emailBlockedReason, clientEmail: d.clientEmail, noEmailAddress: d.noEmailAddress } : d))
    } catch { /* the saved consent above already unlocked Send; the server re-checks on send */ }
  }

  // Client-side checks mirror the server's (it re-validates on send).
  const recips = splitRecipients(to)
  const badRecip = recips.find((r) => !EMAIL.test(r))
  const toLf = draft?.recipient?.kind === 'LITIGATION_FRIEND'
  const fieldErr: Partial<Record<Field, string>> = {
    to: !recips.length ? toRequiredMessage(toLf) : badRecip ? `“${badRecip}” isn’t a valid email address.` : serverField.to,
    subject: !subject.trim() ? 'Enter a subject.' : serverField.subject,
    body: !body.trim() ? 'Write the covering message.' : serverField.body,
  }
  // B3 — sending rules (mirrors the server's).
  const wordingBlocked = !!draft && draft.wordingReady === false
  const noEmail = !!draft?.noEmailAddress
  // (Audit fix 1) The client's email changed since consent → not even the old address.
  const consentOk = !!draft && (draft.emailAllowed === undefined || (recipientsMatchConsent(draft.consent?.email, recips) && !draft.consentChanged))
  // (Audit fix 2) The client's date of birth is missing / unreadable, so the
  // server can't tell whether this goes to a litigation friend → no email.
  const capacityUnknown = !!draft?.recipient?.capacityUnknown
  const emailBlocked = wordingBlocked || noEmail || !consentOk || capacityUnknown
  const invalid = !!(fieldErr.to || fieldErr.subject || fieldErr.body)
  const shown = (f: Field): string | undefined => (touched || serverField[f] ? fieldErr[f] : undefined)
  const edit = (f: Field, set: (v: string) => void) => (v: string) => { set(v); if (serverField[f]) setServerField({ ...serverField, [f]: undefined }) }

  const send = async (): Promise<void> => {
    setTouched(true)
    if (invalid || emailBlocked || sending) return
    setSending(true); setError(null); setConnectPrompt(null)
    // (Follow-ups) A bill shows its real progress: "Preparing PDF…" (made ready
    // on the server, usually already rendered while this window was open) →
    // "Sending…". The window stays open (no close, no Cancel) until it's done.
    if (!creditNote) {
      setPhase('preparing')
      try { await prepareBillSendApi(bill.id) } catch (e) {
        setSending(false); setPhase(null)
        setError(errorText(e, 'The bill PDF couldn’t be prepared, so nothing was sent. Please try again.'))
        return
      }
    }
    setPhase('sending')
    const r = creditNote ? await sendCreditNoteApi(creditNote.id, { to, ccMe, subject, body, idempotencyKey: sendKey }) : await sendBillApi(bill.id, { to, ccMe, subject, body, idempotencyKey: sendKey })
    setSending(false); setPhase(null)
    if (r.ok) { if (bill.case?.id) emitCaseChange(bill.case.id, 'bill'); onSent(r.data); return }
    // Not sent — everything typed is kept.
    if (r.needsConnect) { setConnectPrompt('connect'); return }
    if (r.needsReconnect) { setConnectPrompt('reconnect'); setError(r.error); return }
    if (r.field) { setServerField({ [r.field]: r.error }); return }
    setError(r.error)
  }

  const resend = draft?.delivery
  const consentClientId = draft?.clientId ?? bill.case?.clientId ?? null
  // (B4 follow-up 2) A child / protected party → their litigation friend: the
  // To, the consent (recorded for THEM inline) and the window's title.
  const lf = draft?.recipient?.kind === 'LITIGATION_FRIEND' ? draft.recipient : null
  const recipientName = lf?.name ? `${lf.name} (litigation friend)` : draft?.clientName || bill.letterhead.client.name || 'the client'
  const caseId = bill.case?.id ?? null
  const recordLfConsent = lf && !lf.missing && caseId ? (b: { date: string; email: string; method: string }) => recordLitigationFriendConsentApi(caseId, b) : undefined
  return (
    <ModalFrame
      onClose={() => { if (!sending) requestClose() }}
      dismissible={!sending}
      size="lg"
      portal
      scrollBody
      eyebrow={creditNote ? 'Send credit note' : 'Send bill'}
      eyebrowIcon={<BIcon name="mail" size={14} />}
      pills={<span className={HERO_PILL_SOFT}>{docNumber}</span>}
      title={`Send ${docNumber} to ${recipientName}`}
      subtitle={`Sent from your Outlook mailbox with the ${noun} PDF attached, and logged on this case’s activity`}
      bodyClassName="flex flex-col gap-[14px]"
      footerRight={<>
        <button type="button" className={B_SECONDARY} disabled={sending} onClick={requestClose}>Cancel</button>
        {onUsePost && draft && <button type="button" className={B_SECONDARY} disabled={sending || wordingBlocked} onClick={onUsePost}><BIcon name="memo" size={14} />Mark as sent by post / handed over</button>}
        <button type="button" className={B_PRIMARY} disabled={!draft || sending || emailBlocked || (touched && invalid)} onClick={() => { void send() }}>
          <BIcon name="send" size={14} />{sending ? (phase === 'preparing' ? 'Preparing PDF…' : 'Sending…') : resend ? 'Send again' : creditNote ? 'Send credit note' : 'Send bill'}
        </button>
      </>}
    >
      {loadErr && <ActionBox alert tone="danger" title="The email couldn’t be prepared" message={loadErr} />}
      {/* ★ NO LAYOUT SHIFT: while the draft loads, the SAME form card renders
          with empty, disabled fields (same labels, inputs, 8-row message,
          attachment row) — not a short skeleton the form then outgrew, which
          made the centred modal jump. */}
      {!draft && !loadErr && (
        <div role="status" aria-label="Preparing the email" className={`${CARD} p-4 grid gap-[14px]`}>
          <FormField label="To" required variant="info" htmlFor="sb-to-loading">
            <input id="sb-to-loading" type="text" className={INPUT} value="" disabled readOnly />
          </FormField>
          <div className="inline-flex items-center gap-[9px] text-[13px] w-fit">
            <span aria-hidden="true" className="relative w-8 h-[18px] rounded-full bg-casevo-border" />
            <Skel className="h-[12px] w-56" />
          </div>
          <FormField label="Subject" required variant="info" htmlFor="sb-subject-loading">
            <input id="sb-subject-loading" type="text" className={INPUT} value="" disabled readOnly />
          </FormField>
          <FormField label="Message" required variant="info" htmlFor="sb-body-loading">
            <MessageTextarea id="sb-body-loading" className={INPUT} value="" disabled readOnly />
          </FormField>
          <div className="flex items-center gap-[10px] rounded-[10px] border border-casevo-border-soft bg-casevo-input px-3 py-[9px]">
            <span className="w-[28px] h-[28px] rounded-[8px] bg-casevo-mint shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px]"><Skel className="inline-block align-middle h-[11px] w-40" /></div>
              <div className={NOTE}>The {noun} PDF, attached automatically, exactly as it prints</div>
            </div>
          </div>
        </div>
      )}
      {draft && <>
        {/* (Audit fix batch 1, Low 9) The PDF names a different litigation friend from today's. */}
        {/* (Audit fix batch 2) Every note here is the shared ActionBox shell. */}
        {draft.addresseeWarning && (
          <div data-addressee-warning><ActionBox title="Check who this goes to" message={draft.addresseeWarning} /></div>
        )}
        {connectPrompt && (
          <ActionBox alert title={connectPrompt === 'connect' ? 'Connect your Microsoft account first to send email' : (error ?? 'Your Microsoft connection expired — please reconnect')}
            message="Nothing was sent; your message is kept."
            actions={[{ label: 'Open Settings', kind: 'primary', onClick: () => guardClose(() => { onClose(); void navigateTo({ view: 'settings', params: { section: 'email' } }) }) }]} />
        )}
        {resend && (
          <ActionBox tone="info" variant="slim" title={`Already sent on ${fmtDate(resend.at, 'long')}${resend.by ? ` by ${resend.by}` : ''}.`}
            message="Sending it again won’t change that delivery date." />
        )}
        {/* B3 — the wording must be approved before any bill goes out. */}
        {wordingBlocked && (
          <ActionBox title="The bill wording isn’t approved yet"
            message={<>{draft.wordingProblem || `A solicitor at your firm must approve the bill wording before the ${noun} can be sent (Settings → Bill wording).`}{!isAdmin && <> Ask an admin.</>}</>}
            actions={isAdmin ? [{ label: 'Open Settings › Bill wording', kind: 'primary', onClick: () => guardClose(() => { onClose(); try { localStorage.setItem('casevo_admin_section', 'billWording') } catch { /* storage off */ } void navigateTo({ view: 'admin', params: { section: 'billWording' } }) }) }] : undefined} />
        )}
        {/* B3 — "Client has no email address": post or hand it over instead. */}
        {lf?.missing && !wordingBlocked && (
          <div data-no-litigation-friend>
            <ActionBox title="No litigation friend is recorded" message={<>For a child or protected party the {noun} goes to their litigation friend — add them in Client details, or send the {noun} by post or hand it over, then record it with “Mark as sent by post / handed over”.</>} />
          </div>
        )}
        {capacityUnknown && !wordingBlocked && (
          <div data-capacity-unknown>
            <ActionBox title={capacityUnknownMessage(noun)} message={<>You can still send the {noun} by post or hand it over, then record it with “Mark as sent by post / handed over”.</>} />
          </div>
        )}
        {lf && !lf.missing && (
          <p className={`${NOTE} m-0`}>For a child or protected party the {noun} goes to their litigation friend, {lf.name}{lf.relationship ? ` (${lf.relationship})` : ''}, at the address they agreed to.</p>
        )}
        {noEmail && !wordingBlocked && !lf?.missing && (
          <div data-no-email>
            <ActionBox title={NO_EMAIL_ON_FILE} message={<>Send the {noun} by post or hand it over, then record it with “Mark as sent by post / handed over”.</>} />
          </div>
        )}
        {/* (Client-sync batch) The case's copy of the email / phone / address
            differs from the client record (which bills use) → a calm note.
            Updating the record's email re-reads the To + consent facts. */}
        {bill.case?.id && <ContactMismatchNote caseId={bill.case.id} onChanged={(g, direction) => {
          // Only the RECORD's email changing moves To (+ its consent facts).
          if (g !== 'email' || direction !== 'toRecord') return
          void fetchDraft().then((d) => {
            setDraft((cur) => (cur ? { ...cur, to: d.to, clientEmail: d.clientEmail, clientHasEmail: d.clientHasEmail, consent: d.consent, consentChanged: d.consentChanged, emailAllowed: d.emailAllowed, emailBlockedReason: d.emailBlockedReason, noEmailAddress: d.noEmailAddress } : d))
            setTo(d.to)
          }).catch(() => undefined)
        }} />}
        {/* B3 — email consent: the To address must be the consented one.
            B3c — recorded right here (ConsentInline), never a pop-up. */}
        {!noEmail && !wordingBlocked && !consentOk && !capacityUnknown && (
          <ConsentInline clientId={lf ? null : consentClientId} recordConsent={recordLfConsent} partyNoun={lf ? 'the litigation friend' : undefined} documentsNoun={creditNote ? 'credit notes' : 'bills'} clientName={lf?.name ?? draft.clientName} to={recips} disabled={sending}
            facts={{ consentEmail: draft.consent?.email ?? null, clientEmail: draft.clientEmail ?? null, consentChanged: !!draft.consentChanged }}
            onUsePost={onUsePost} onSaved={(st, email, dateIso, method) => { void consentSaved(st, email, dateIso, method) }} />
        )}
        {!noEmail && !wordingBlocked && consentOk && recorded && (
          <ActionBox live tone="done" variant="slim" title={`Consent recorded for ${recorded}.`} message={`You can send the ${noun} now.`} />
        )}
        {!noEmail && !wordingBlocked && consentOk && !recorded && draft.consent && (
          <p className={`${NOTE} m-0`}>The client agreed to receive bills by email at {draft.consent.email} on {fmtDate(draft.consent.at, 'long')}.</p>
        )}
        {!noEmail && !draft.clientHasEmail && (
          <ActionBox
            title={lf ? `There’s no email address recorded for ${lf.name || 'the litigation friend'}` : `There’s no email address on ${draft.clientName || 'the client'}’s record`}
            message={lf
              ? <>Enter one below to send the {noun}. It’s only used for this email; add it in Client details to keep it.</>
              : <>Enter one below to send the {noun}. It’s only used for this email; add it to the client’s details to keep it.</>}
            actions={sending ? undefined : [{ label: 'Enter an email address', kind: 'link', arrow: true, onClick: () => toRef.current?.focus() }]} />
        )}

        {!noEmail && <div className={`${CARD} p-4 grid gap-[14px]`}>
          <FormField label="To" required variant="info" htmlFor="sb-to">
            <input id="sb-to" ref={toRef} type="text" className={INPUT} value={to} onChange={(e) => edit('to', setTo)(e.target.value)} disabled={sending}
              placeholder="client@example.com (comma-separate for several)" autoComplete="off" autoFocus={!draft.clientHasEmail}
              aria-invalid={!!shown('to') || undefined} aria-describedby={shown('to') ? 'sb-to-err' : undefined} />
            <span id="sb-to-err"><FieldError message={shown('to')} /></span>
          </FormField>
          <label className="relative inline-flex items-center gap-[9px] text-[13px] text-casevo-forest cursor-pointer w-fit">
            <input type="checkbox" role="switch" checked={ccMe} onChange={(e) => setCcMe(e.target.checked)} disabled={sending} className="sr-only peer" />
            <span aria-hidden="true" className={`relative w-8 h-[18px] rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--casevo-focus)] ${ccMe ? 'bg-casevo-primary' : 'bg-casevo-border'}`}>
              <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-casevo-surface transition-[left] ${ccMe ? 'left-[16px]' : 'left-[2px]'}`} />
            </span>
            <span>Send a copy to me <span className="text-casevo-text-soft">({draft.senderEmail})</span></span>
          </label>
          <FormField label="Subject" required variant="info" htmlFor="sb-subject">
            <input id="sb-subject" type="text" className={INPUT} value={subject} onChange={(e) => edit('subject', setSubject)(e.target.value)} disabled={sending} maxLength={300}
              aria-invalid={!!shown('subject') || undefined} aria-describedby={shown('subject') ? 'sb-subject-err' : undefined} />
            <span id="sb-subject-err"><FieldError message={shown('subject')} /></span>
          </FormField>
          <FormField label="Message" required variant="info" htmlFor="sb-body">
            <MessageTextarea id="sb-body" className={INPUT} value={body} onChange={(e) => edit('body', setBody)(e.target.value)} disabled={sending} maxLength={5000}
              aria-invalid={!!shown('body') || undefined} aria-describedby={shown('body') ? 'sb-body-err' : undefined} />
            <span id="sb-body-err"><FieldError message={shown('body')} /></span>
          </FormField>
          <div className="flex items-center gap-[10px] rounded-[10px] border border-casevo-border-soft bg-casevo-input px-3 py-[9px]">
            <span className="w-[28px] h-[28px] rounded-[8px] bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] grid place-items-center shrink-0"><BIcon name="memo" size={13} /></span>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-casevo-forest truncate">{draft.attachmentName}</div>
              <div className={NOTE}>The {noun} PDF, attached automatically, exactly as it prints</div>
            </div>
          </div>
        </div>}
        {error && !connectPrompt && <p className="m-0 text-[13px] text-casevo-danger" role="alert">{error}</p>}
        {/* (Audit fix batch 2, Low 12) The live region is ALWAYS mounted (a
            region that arrives already holding text often isn't read) and says
            the real step, as the button does. */}
        <p className="m-0 text-[12.5px] text-casevo-mid flex items-center gap-2 empty:hidden" aria-live="polite" data-send-progress={phase ?? ''}>
          {sending && <span className="inline-block w-3 h-3 rounded-full border-2 border-casevo-mid border-t-transparent animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {sending ? (phase === 'preparing' ? `Preparing the ${noun} PDF…` : 'Sending the email…') : ''}
        </p>
      </>}
    </ModalFrame>
  )
}

export default SendBillModal
